import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { VoiceModuleHandler } from './voice-handler.interface';
import { VoiceIntent, VoiceExecutionResult, SuggestedAction } from '../models';
import { RoomsService } from '../../../modules/room-reservations/services/rooms.service';
import { RoomReservationsService, SLOT_TAKEN_MESSAGE } from '../../../modules/room-reservations/services/room-reservations.service';
import {
  addDaysToDate,
  isoToWallTime,
  rangeBoundsForZones,
  safeTimezone,
  wallTimeToIso,
} from '../../../modules/room-reservations/utils/room-time';
import { SubscriptionFeaturesService } from '../../../services/subscription-features.service';
import { AuthService } from '../../auth/auth.service';
import { Room, RoomReservation, Profile } from '../../../models/interfaces';
import { pickVariant } from './response-variants.util';

interface RoomResolution {
  room: Room | null;
  error?: string;
}

/**
 * MOKA-207 Phase 4, Lot 2 : handler vocal du module Salles de réunion.
 *
 * `rooms.reserveRoom { date (requis, YYYY-MM-DD), startTime?, endTime?, roomName?, title? }`
 * — contrat figé côté edge function (commit c38b9fec), `title` ajouté par MOKA-266 :
 * l'objet de la réunion dicté ou écrit (« objet : … », « pour le point produit »).
 * Sans objet, titre neutre « Réservation de {prénom nom} », juste à l'écrit comme à l'oral.
 * Plusieurs créneaux dans une phrase = un intent par créneau (`additionalIntents`),
 * exécutés en lot par `VoiceService` avec récapitulatif et échec partiel explicite.
 *
 * Décisions notables (cf. rapport de fin de tâche pour le détail) :
 * - `startTime` absent → follow-up (jamais d'heure par défaut inventée à
 *   partir de rien, contrairement à `endTime` qui, lui, a un défaut observé
 *   ailleurs dans l'app).
 * - `endTime` absent mais `startTime` fourni → défaut 1h, la même durée que
 *   `AddEditReservationComponent` pré-remplit (09:00 → 10:00) quand
 *   l'utilisateur ne fait pas de drag-to-create.
 * - Salle non précisée : résolue automatiquement seulement s'il n'existe
 *   qu'une seule salle dans l'organisation ; sinon follow-up listant les noms.
 * - Conflit de créneau (contrainte d'exclusion Postgres, `room_reservations_no_overlap`,
 *   déjà remontée par `RoomReservationsService.createReservation` sous forme
 *   d'un message explicite) : ÉCHEC EXPLICITE, jamais de demi-succès. Comme la
 *   disponibilité des salles/créneaux peut se calculer à coût raisonnable
 *   (2 requêtes au total : `getBookableRooms` déjà chargée + `getReservationsInRange`
 *   sur conflit uniquement, aucune requête en cascade par salle), des
 *   alternatives concrètes (autre salle au même horaire, créneau adjacent sur
 *   la même salle) sont calculées et injectées dans le message. Elles sont
 *   restituées en TEXTE (lu/affiché), PAS en boutons cliquables : le mécanisme
 *   de boutons (`VoiceExecutionResult` → `VoiceMessageOption[]`) n'existe
 *   aujourd'hui que pour la clarification "projet manquant"
 *   (`needsProjectClarification`, câblée en dur dans `VoiceService.executeIntent`)
 *   — l'étendre à un cas générique aurait débordé du périmètre de ce lot
 *   (deux handlers + enregistrement router) et touché des fichiers coeur du
 *   module vocal en cours de modification par d'autres agents dans cette
 *   session. Le texte reste lisible ET dicible en TTS, donc l'exigence
 *   "jamais de demi-succès silencieux, motif clair" est respectée telle quelle.
 *
 * MOKA-241 (lot 0 du module Salles) :
 * - les Salles font partie du module Calendrier : sans lui, refus explicite ;
 * - seules les salles ACTIVES sont proposées / résolues ;
 * - `date`, `startTime`, `endTime` sont des heures MURALES du fuseau de la
 *   salle (`rooms.timezone`), converties en instants avant l'écriture ; les
 *   alternatives sont calculées en instants, puis relues dans ce fuseau.
 */
@Injectable({ providedIn: 'root' })
export class RoomsVoiceHandler implements VoiceModuleHandler {
  private auth = inject(AuthService);
  private roomsService = inject(RoomsService);
  private reservationsService = inject(RoomReservationsService);
  private subscriptionFeatures = inject(SubscriptionFeaturesService);

  /**
   * Heures ouvrées des créneaux PROPOSÉS par l'assistant en cas de conflit
   * (8 h – 20 h). Sans lien avec l'affichage : la grille du calendrier
   * s'élargit selon les réservations (utils/room-grid.ts).
   */
  private readonly BUSINESS_START_HOUR = 8;
  private readonly BUSINESS_END_HOUR = 20;

  /** Durée par défaut quand seule `startTime` est fournie — reprend le pré-remplissage 09:00→10:00 d'`AddEditReservationComponent` */
  private readonly DEFAULT_DURATION_MIN = 60;

  private readonly TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

  /** Longueur maximale du titre repris de la phrase (aucune contrainte en base, garde-fou d'affichage). */
  private readonly TITLE_MAX_LENGTH = 200;

  /** Message exact lancé par `RoomReservationsService.createReservation` sur conflit (code Postgres 23P01) */
  private readonly SLOT_TAKEN_MESSAGE = SLOT_TAKEN_MESSAGE;

  readonly supportedEndpoints = ['rooms.reserveRoom'];

  async execute(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    switch (intent.endpoint) {
      case 'rooms.reserveRoom':
        return this.handleReserveRoom(intent);
      default:
        return { success: false, message: `Action salles non supportée: ${intent.endpoint}` };
    }
  }

  private async handleReserveRoom(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    const profile = this.auth.profile;
    if (!profile) {
      return { success: false, message: 'Utilisateur non connecté' };
    }

    // Décision 7 du module Salles : même drapeau d'abonnement que le Calendrier.
    if (!this.subscriptionFeatures.moduleEnabled('calendars_enable')) {
      return {
        success: false,
        message: 'La réservation de salles fait partie du module Calendrier, qui n\'est pas inclus dans votre abonnement.'
      };
    }

    const date = intent.params['date'] as string;
    const startTime = intent.params['startTime'] as string | undefined;
    const endTimeParam = intent.params['endTime'] as string | undefined;
    const roomName = (intent.params['roomName'] as string | undefined)?.trim();
    const title = cleanReservationTitle(intent.params['title'], this.TITLE_MAX_LENGTH);

    if (startTime && !this.TIME_REGEX.test(startTime)) {
      return { success: false, message: `Heure de début invalide : "${startTime}" (format attendu HH:mm)` };
    }
    if (endTimeParam && !this.TIME_REGEX.test(endTimeParam)) {
      return { success: false, message: `Heure de fin invalide : "${endTimeParam}" (format attendu HH:mm)` };
    }

    // Les salles désactivées ne sont plus réservables : on ne les propose pas.
    const rooms = await firstValueFrom(this.roomsService.getBookableRooms());
    if (rooms.length === 0) {
      return { success: false, message: 'Aucune salle réservable n\'est configurée pour votre organisation.' };
    }

    const roomResolution = this.resolveRoom(rooms, roomName);
    if (!roomResolution.room) {
      return {
        success: false,
        message: roomResolution.error!,
        followUp: {
          question: 'Quelle salle souhaitez-vous réserver ?',
          endpoint: 'rooms.reserveRoom',
          params: {
            date,
            ...(startTime ? { startTime } : {}),
            ...(startTime && endTimeParam ? { endTime: endTimeParam } : {}),
            ...(title ? { title } : {})
          }
        }
      };
    }
    const room = roomResolution.room;
    const dateLabel = this.formatDate(date);

    if (!startTime) {
      return {
        success: false,
        message: `Il me manque l'heure pour réserver "${room.name}" le ${dateLabel}.`,
        followUp: {
          question: 'À quelle heure ?',
          endpoint: 'rooms.reserveRoom',
          params: { date, roomName: room.name, ...(title ? { title } : {}) }
        }
      };
    }

    const endTime = endTimeParam ?? this.addDefaultDuration(startTime);
    if (endTime <= startTime) {
      return { success: false, message: 'L\'heure de fin doit être postérieure à l\'heure de début.' };
    }

    // Heures dictées = heures murales de la salle.
    const timezone = safeTimezone(room.timezone);
    const startsAt = wallTimeToIso(date, startTime, timezone);
    const endsAt = wallTimeToIso(date, endTime, timezone);

    try {
      const created = await firstValueFrom(
        this.reservationsService.createReservation({
          room_id: room.id,
          title: title ?? defaultReservationTitle(profile),
          starts_at: startsAt,
          ends_at: endsAt,
          source: 'voice'
        })
      );

      const titleSuffix = title ? ` — « ${title} »` : '';
      return {
        success: true,
        message: pickVariant([
          `Salle "${room.name}" réservée le ${dateLabel} de ${startTime} à ${endTime}`,
          `C'est fait : "${room.name}" est réservée le ${dateLabel}, de ${startTime} à ${endTime}`,
          `Réservation confirmée pour "${room.name}" le ${dateLabel} (${startTime}-${endTime})`
        ]) + titleSuffix,
        data: { reservationId: created.id, roomId: room.id, roomName: room.name, date, startTime, endTime, title: title ?? null }
      };
    } catch (error) {
      if (error instanceof Error && error.message === this.SLOT_TAKEN_MESSAGE) {
        const suggestion = await this.buildConflictSuggestion(rooms, room, date, startTime, endTime, title);
        // MOKA-266 : la date et l'occupation réelle, pour qu'un échec dans un lot
        // (« le 15 : salle déjà prise de 14 h à 15 h ») se comprenne seul.
        const busy = suggestion.busy ? ` de ${suggestion.busy}` : ' sur ce créneau';
        return {
          success: false,
          message: `Le ${dateLabel}, la salle "${room.name}" est déjà prise${busy}.${suggestion.text}`,
          suggestedActions: suggestion.actions
        };
      }
      const msg = error instanceof Error ? error.message : 'erreur inconnue';
      return { success: false, message: `Erreur lors de la réservation : ${msg}` };
    }
  }

  private resolveRoom(rooms: Room[], roomName?: string): RoomResolution {
    if (roomName) {
      const match = this.findRoomByName(rooms, roomName);
      if (match) return { room: match };
      const names = rooms.map(r => r.name).join(', ');
      return { room: null, error: `Salle "${roomName}" introuvable. Salles disponibles : ${names}.` };
    }

    if (rooms.length === 1) {
      return { room: rooms[0] };
    }

    const names = rooms.map(r => r.name).join(', ');
    return { room: null, error: `Plusieurs salles existent (${names}) : précisez laquelle réserver.` };
  }

  /** Même stratégie de résolution floue que `ProjectVoiceHandler.findTaskByTitle` */
  private findRoomByName(rooms: Room[], name: string): Room | null {
    const normalized = name.toLowerCase();

    const exact = rooms.find(r => r.name.toLowerCase() === normalized);
    if (exact) return exact;

    const includes = rooms.filter(r => r.name.toLowerCase().includes(normalized));
    if (includes.length === 1) return includes[0];

    const reverse = rooms.filter(r => normalized.includes(r.name.toLowerCase()));
    if (reverse.length === 1) return reverse[0];

    if (includes.length > 1) {
      includes.sort((a, b) =>
        Math.abs(a.name.length - name.length) - Math.abs(b.name.length - name.length)
      );
      return includes[0];
    }

    return null;
  }

  private addDefaultDuration(startTime: string): string {
    const [h, m] = startTime.split(':').map(Number);
    return this.minutesToLabel(h * 60 + m + this.DEFAULT_DURATION_MIN);
  }

  private minutesToLabel(totalMinutes: number): string {
    const clamped = Math.min(Math.max(totalMinutes, 0), 23 * 60 + 59);
    const h = Math.floor(clamped / 60).toString().padStart(2, '0');
    const m = (clamped % 60).toString().padStart(2, '0');
    return `${h}:${m}`;
  }

  private toMinutes(time: string): number {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  }

  /** Minutes depuis minuit, horloge murale du fuseau de la salle. */
  private toMinutesFromIso(iso: string, timezone: string): number {
    return isoToWallTime(iso, timezone).minutes;
  }

  private formatDate(dateStr: string): string {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  }

  /** « jeu. 15 oct. » — libellé court des boutons d'alternative. */
  private formatDateShort(dateStr: string): string {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  }

  /**
   * Calcule des alternatives à coût raisonnable (1 requête `getReservationsInRange`,
   * `rooms` déjà en mémoire) : une autre salle libre au même horaire, ou un
   * créneau adjacent libre sur la même salle (juste après la réservation
   * conflictuelle, sinon juste avant), borné aux heures d'ouverture affichées
   * par le module (8h-20h).
   *
   * MOKA-208 P2 #2 : retourne AUSSI les alternatives sous forme d'actions
   * rejouables (`SuggestedAction`), pour que l'utilisateur clique au lieu de
   * redicter toute la demande. Le texte reste renseigné : il sert de repli
   * (lecture TTS, reprise d'historique où les boutons sont volatils).
   */
  private async buildConflictSuggestion(
    rooms: Room[],
    targetRoom: Room,
    date: string,
    startTime: string,
    endTime: string,
    title?: string
  ): Promise<{ text: string; actions: SuggestedAction[]; busy?: string }> {
    const fallback = { text: ' Essayez un autre horaire ou une autre salle.', actions: [] };
    // L'objet suit les alternatives : cliquer une suggestion ne doit pas le perdre.
    const titleParam = title ? { title } : {};
    // Date courte dans les boutons : dans un lot, deux échecs peuvent porter sur deux jours différents.
    const dayShort = this.formatDateShort(date);

    const timezone = safeTimezone(targetRoom.timezone);
    const zones = rooms.map(r => safeTimezone(r.timezone));

    let dayReservations: RoomReservation[];
    try {
      const { start, end } = rangeBoundsForZones(date, addDaysToDate(date, 1), zones);
      dayReservations = await firstValueFrom(this.reservationsService.getReservationsInRange(start, end));
    } catch {
      return fallback;
    }

    const requestedStart = this.toMinutes(startTime);
    const requestedEnd = this.toMinutes(endTime);
    const durationMin = requestedEnd - requestedStart;

    const suggestions: string[] = [];
    const actions: SuggestedAction[] = [];

    // Chevauchement en INSTANTS, pour la salle cible…
    const overlapsInstant = (res: RoomReservation, startIso: string, endIso: string) =>
      Date.parse(res.starts_at) < Date.parse(endIso) && Date.parse(res.ends_at) > Date.parse(startIso);
    const requestedStartIso = wallTimeToIso(date, startTime, timezone);
    const requestedEndIso = wallTimeToIso(date, endTime, timezone);
    const overlaps = (res: RoomReservation) => overlapsInstant(res, requestedStartIso, requestedEndIso);

    // … et pour une autre salle : le même horaire MURAL, dans le fuseau de cette salle.
    const freeRoom = rooms.find(r => {
      if (r.id === targetRoom.id) return false;
      const tz = safeTimezone(r.timezone);
      const s = wallTimeToIso(date, startTime, tz);
      const e = wallTimeToIso(date, endTime, tz);
      return !dayReservations.some(res => res.room_id === r.id && overlapsInstant(res, s, e));
    });
    if (freeRoom) {
      suggestions.push(`la salle "${freeRoom.name}" au même horaire`);
      actions.push({
        id: `room:${freeRoom.id}:${date}`,
        label: `${freeRoom.name} — ${dayShort}, ${startTime}-${endTime}`,
        endpoint: 'rooms.reserveRoom',
        params: { date, startTime, endTime, roomName: freeRoom.name, ...titleParam }
      });
    }

    const sameRoomReservations = dayReservations
      .filter(res => res.room_id === targetRoom.id)
      .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at));

    const conflicting = sameRoomReservations.find(overlaps);
    // Occupation réelle sur le créneau demandé (toutes les réservations qui le chevauchent).
    const overlapping = sameRoomReservations.filter(overlaps);
    const busy = overlapping.length > 0
      ? `${this.minutesToLabel(this.toMinutesFromIso(overlapping[0].starts_at, timezone))} à ${this.minutesToLabel(
          Math.max(...overlapping.map(res => this.toMinutesFromIso(res.ends_at, timezone)))
        )}`
      : undefined;

    if (conflicting) {
      const afterStart = this.toMinutesFromIso(conflicting.ends_at, timezone);
      const afterEnd = afterStart + durationMin;
      const nextAfter = sameRoomReservations.find(res =>
        res !== conflicting && this.toMinutesFromIso(res.starts_at, timezone) >= afterStart
      );
      const afterFree = afterEnd <= this.BUSINESS_END_HOUR * 60
        && (!nextAfter || this.toMinutesFromIso(nextAfter.starts_at, timezone) >= afterEnd);

      if (afterFree) {
        suggestions.push(`la même salle de ${this.minutesToLabel(afterStart)} à ${this.minutesToLabel(afterEnd)}`);
        actions.push({
          id: `slot:${targetRoom.id}:${date}:${afterStart}`,
          label: `${targetRoom.name} — ${dayShort}, ${this.minutesToLabel(afterStart)}-${this.minutesToLabel(afterEnd)}`,
          endpoint: 'rooms.reserveRoom',
          params: {
            date,
            startTime: this.minutesToLabel(afterStart),
            endTime: this.minutesToLabel(afterEnd),
            roomName: targetRoom.name,
            ...titleParam
          }
        });
      } else {
        const beforeEnd = this.toMinutesFromIso(conflicting.starts_at, timezone);
        const beforeStart = beforeEnd - durationMin;
        const prevBefore = [...sameRoomReservations].reverse().find(res =>
          res !== conflicting && this.toMinutesFromIso(res.ends_at, timezone) <= beforeEnd
        );
        const beforeFree = beforeStart >= this.BUSINESS_START_HOUR * 60
          && (!prevBefore || this.toMinutesFromIso(prevBefore.ends_at, timezone) <= beforeStart);

        if (beforeFree) {
          suggestions.push(`la même salle de ${this.minutesToLabel(beforeStart)} à ${this.minutesToLabel(beforeEnd)}`);
          actions.push({
            id: `slot:${targetRoom.id}:${date}:${beforeStart}`,
            label: `${targetRoom.name} — ${dayShort}, ${this.minutesToLabel(beforeStart)}-${this.minutesToLabel(beforeEnd)}`,
            endpoint: 'rooms.reserveRoom',
            params: {
              date,
              startTime: this.minutesToLabel(beforeStart),
              endTime: this.minutesToLabel(beforeEnd),
              roomName: targetRoom.name,
              ...titleParam
            }
          });
        }
      }
    }

    if (suggestions.length === 0) {
      return { ...fallback, busy };
    }

    return {
      text: ` Essayez : ${suggestions.slice(0, 2).join(', ou ')}.`,
      actions: actions.slice(0, 2),
      busy
    };
  }
}

/**
 * Titre par défaut d'une réservation faite avec Koffi, sans objet précisé.
 * Neutre quant au canal : la même phrase peut être dite ou écrite.
 */
export function defaultReservationTitle(profile: Pick<Profile, 'first_name' | 'last_name'>): string {
  const name = `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim();
  return name ? `Réservation de ${name}` : 'Réservation';
}

/**
 * Objet de réunion extrait de la phrase (`params.title`) : espaces normalisés,
 * guillemets et ponctuation finale retirés, longueur bornée. `undefined` si vide
 * ou absent — le titre par défaut s'applique alors.
 */
export function cleanReservationTitle(raw: unknown, maxLength = 200): string | undefined {
  if (typeof raw !== 'string') return undefined;
  // Guillemets et ponctuation finale peuvent s'entremêler (« X » .) : on retire les deux ensemble.
  const cleaned = raw
    .replace(/\s+/g, ' ')
    .replace(/^["'«“‘\s]+/, '')
    .replace(/["'»”’\s.,;:!]+$/, '')
    .trim();
  if (!cleaned) return undefined;
  return cleaned.length > maxLength ? cleaned.slice(0, maxLength).trimEnd() : cleaned;
}
